/// REACT
import React, { useEffect, useMemo, useState } from 'react';

//GRAPHQL
import { useQuery } from '@apollo/client';
import { getFragmentData } from '../../../__generated__';

/// MUI AND DIV COMPONENTS
import { GridColDef, GridNoRowsOverlay, GridRowModel } from '@mui/x-data-grid';
import { IconButton, Typography, Stack, Paper, Tooltip, Grid, Chip } from '@mui/material';
import { AddBox, Close, Description } from '@mui/icons-material';
import { format } from 'date-fns';
import { HeaderActionType, StandardFilterOption, TablePaginated, TableToolBarStacked } from 'ui';
///QUERIES, MUTATIONS, CONTEXT, HOOKS
import { PersonDataType } from '../../../context/SelectedPersonContext';
import { useSlideInContext, FetchMoreCallbackArgs } from "ui";
import { ADD_USER } from 'ft-shared-components';
import { SELECTED_PERSON_FRAGMENT } from '../../../features/graphql/query/person/fragments/peopleFragments';
import usePersonStore from '../../store/personStore';
import usePersonAuthStore, { UserRoleHierarchy } from "../../store/personAuthStore"
import { canAddStudentAndStaff, GET_PEOPLE_WITH_SESSION_COUNT } from 'ft-shared-graphql';
import { orderBySessionsRun } from '../../../tools';
const DEFAULT_PAGE_SIZE = 100;
///NOTE: THIS IS TO ORGANIZE THE RANKINGS AND ENSURE THAT PEOPLE CAN ONLY PUNCH DOWN, NOT UP

const TABLE_COLUMNS: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: .5, sortable: true, },
    { field: "lastName", headerName: "Last Name", flex: .5, sortable: true },
    {
        field: "locations", headerName: "Location(s)", flex: 1, sortable: true, resizable: false,
        renderCell: ({ value }) => {
            const hasLocations = value.length > 0
            if (hasLocations) {
                return (
                    <Grid container spacing={1} sx={{ paddingTop: 2, paddingBottom: 2, justifyContent: "start" }}>
                        {value.map((location: string) => (
                            <Grid key={`tags-chip-${location}`} style={{ marginTop: 1 }}>
                                <Chip className="chip" label={location} />
                            </Grid>
                        ))}
                    </Grid>
                )
            } else {
                return (<Typography>Please assign person to a location</Typography>)
            }
        }
    },
    {
        field: "lastRunSession", headerName: "Date of Last Run Session", flex: .5, sortable: true, resizable: false,
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "start", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>Date of Last Run Session</div>
        ), renderCell: ({ value }) => {
            if (!value) return "N/A";
            const datePart = String(value).split("T")[0];

            if (datePart === "0001-01-01") {
                return "N/A";
            }

            return format(new Date(value), "MM/dd/yyyy");
        }
    },
    {
        field: "totalCompleteSessions", headerName: "Total Complete Sessions", flex: .5, sortable: true, resizable: false,
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "start", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>Total Complete Sessions</div>
        )
    },
    {
        field: "sessionsReviewed", headerName: "Total Sessions Reviewed", flex: .5, sortable: true, resizable: false,
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "start", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>Total Sessions Reviewed</div>
        )
    },
]
function mapToTable(data: any) {
    const node = data.node
    const _data = {
        ...node,
        locations: node.locations?.edges?.map((x: any) => x.node.name),
        sessionsReviewed: node.workLists.filter((x: any) => x.status === "Read").length
    }
    return _data
}

type SelectedAction = "DETAILS" | "EXPIRE"

type StaffTableProps = {
    title: string,
    showNew: boolean
    onBack?: () => void
    onSelectedPerson: (record: any, action: SelectedAction) => void
}

export const StaffTable = ({ title, showNew, onBack, onSelectedPerson }: StaffTableProps) => {
    const { handleContent } = useSlideInContext();
    const defaultLocation = usePersonStore(state => state.company.defaultLocation);
    const myId = usePersonStore(state => state.id);
    const companyType = usePersonStore(state => state.company.type)
    const roles = usePersonAuthStore(state => state.roles)
    const myHighestRole = usePersonAuthStore((state) => state.highestRole)
    const [pageEdges, setPageEdges] = useState<any[] | null>(null);

    const { loading, data, fetchMore, refetch } = useQuery(GET_PEOPLE_WITH_SESSION_COUNT, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: "",
            first: DEFAULT_PAGE_SIZE,
            after: null,
            locationId: defaultLocation?.id,
        },
    });
    const [filterOption, setFilterOption] = useState<StandardFilterOption>("ACTIVE");
    const rows = useMemo(() => {
        const edges = pageEdges ?? data?.personStatsPerCompany?.edges ?? []
        // edges.filter(())
        return orderBySessionsRun(edges?.map(mapToTable));
    }, [pageEdges, data])
    const columns: GridColDef[] = useMemo(() => {
        const cols = [...TABLE_COLUMNS]
        cols.push({
            field: "roleType", headerName: "Role / Type", flex: .5, sortable: true,
            renderCell: (params) => {
                const roleType = params.row.role?.clientFacingName
                return <div>{roleType}</div>
            }
        },)

        cols.push({
            field: 'details',
            headerAlign: "center",
            align: "center",
            headerName: 'Details',
            sortable: false,
            resizable: false,
            editable: false,
            width: 80,
            renderCell: (params) => {
                const record = data?.personStatsPerCompany?.edges?.find((edge: any) => {
                    const fragment = getFragmentData(SELECTED_PERSON_FRAGMENT, edge.node);
                    return fragment.id == params.row.id
                })
                return (
                    <IconButton onClick={() => onSelectedPerson(record?.node as PersonDataType, "DETAILS")}>
                        <Description />
                    </IconButton>
                );
            },
        });
        if (canAddStudentAndStaff(roles)) {
            cols.push({
                align: "center",
                headerAlign: "center",
                sortable: false,
                resizable: false,
                editable: false,
                disableColumnMenu: true,
                field: "id",
                width: 80,
                headerName: "Expire",
                renderCell: (params) => {
                    const status = params.row.expired
                    const isSelf = params.row.id === myId;
                    const role = params.row.role.normalizedName
                    const userDoesNotHaveAccess = UserRoleHierarchy[myHighestRole as keyof typeof UserRoleHierarchy] < UserRoleHierarchy[role as keyof typeof UserRoleHierarchy]
                    let disabledMessage;
                    if (isSelf) {
                        disabledMessage = "Cannot Archive Self"
                    } else if (status) {
                        disabledMessage = "User already archived"
                    } else if (userDoesNotHaveAccess) {
                        disabledMessage = "Cannot archive user with more permissions"
                    }
                    else {
                        disabledMessage = ""
                    }
                    const record = data?.personStatsPerCompany?.edges?.find((edge: any) => {
                        const fragment = getFragmentData(SELECTED_PERSON_FRAGMENT, edge.node);
                        return fragment.id == params.row.id
                    })
                    return (
                        <Tooltip title={disabledMessage}>
                            <div>
                                <IconButton onClick={() => { onSelectedPerson(record?.node as PersonDataType, "EXPIRE"); refetch() }}>
                                    <Close
                                        style={{
                                            backgroundColor: status || isSelf || userDoesNotHaveAccess ? "#EEE" : "#8E1C3B",
                                            borderRadius: "100%",
                                            color: status || isSelf || userDoesNotHaveAccess ? "#999" : "#FFF",
                                        }}
                                    />
                                </IconButton>
                            </div>
                        </Tooltip>
                    )
                }
            })
        }
        return cols;
    }, [data, roles])

    const handleFilter = (selectedOption: StandardFilterOption) => {
        setFilterOption(selectedOption);
        // setPageTableState((prevState) => {
        //     const filteredRows = selectedOption === "ACTIVE"
        //         ? prevState.rows.filter((x) => !x.expired)
        //         : selectedOption == "EXPIRED"
        //             ? prevState.rows.filter((x) => x.expired)
        //             : prevState.rows; // Default "ALL"

        //     return {
        //         ...prevState,
        //         status: selectedOption === "ALL" ? "NORMAL" : "FILTERING",
        //         filteredRows,
        //     };
        // });
    }
    const displayRows = useMemo(() => {
        switch (filterOption) {
            case "ACTIVE":
                return rows.filter((x) => !x.expired);
            case "EXPIRED":
                return rows.filter((x) => x.expired);
            default: // "ALL"
                return rows;
        }
    }, [rows, filterOption])

    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        try {
            const res = await fetchMore({
                variables: {
                    query: variables.query ?? "",
                    after: variables.after ?? null,
                    first: variables.first ?? DEFAULT_PAGE_SIZE
                }
            })
            setPageEdges(res?.data?.personStatsPerCompany?.edges ?? [])
        } catch (err) {
            console.log("there is an error: ", err);
        }
    }
    const handleOnNew = () => {
        handleContent(ADD_USER, {
            companyType: companyType,
            onSubmit: () => { setPageEdges(null); refetch() }
        })
    }

    const totalRows = filterOption != "ALL"
        ? displayRows.length
        : data?.personStatsPerCompany?.totalCount ?? 0;
    const headerActions: HeaderActionType[] = [
        {
            name: "Add new User",
            icon: <AddBox />,
            action: handleOnNew,
            isHidden: !showNew
        }
    ]
    return (
        <Paper>
            <TablePaginated
                loading={loading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => (
                    <TableToolBarStacked
                        title={title}
                        subTitle={`@ ${defaultLocation?.name}`}
                        headerActions={headerActions}
                        // newButtonLabel={showNew ? 'Add new User' : ""}
                        searchQuery={searchQuery ?? ""}
                        // onNew={showNew ? handleOnNew : undefined}
                        onBack={onBack}
                        onFilter={handleFilter}
                        filterTableName='standard'
                        onSearch={onSearch}
                        onSearchClear={onSearchClear} />
                )}
                slots={{
                    noRowsOverlay: (data?.location?.totalStaff ?? 0) <= 0
                        ? NoStaffFoundOverlay
                        : GridNoRowsOverlay
                }}
                pageInfo={data?.personStatsPerCompany?.pageInfo}
                pageSize={DEFAULT_PAGE_SIZE}
                columns={columns}
                rows={filterOption != "ALL"
                    ? displayRows
                    : rows}
                autoHeight
                checkboxSelection={false}
                getRowClassName={(params) => params.row.expired ? "Expired" : "Active"}
                totalRows={totalRows}
                onFetchMore={handleFetchMore}
            />
        </Paper>
    )
}



function NoStaffFoundOverlay() {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                You do not have any staff associated with this location. Select Add bew Staff Member to get started or contact your futuresTHRIVE representative.
            </Typography>
        </Stack>
    )
}